import React from "react";
// import { DataTable, ITableColumn } from "../../../Components/Tables/Table";
import { Avatar, Button, useTheme } from "@mui/material";
import {
  IDataTableProps,
  MobileLogoRenderType,
  RenderType,
} from "../../../Components/Table/interfaces/IDataTableProps";
import { DataTable } from "../../../Components/Table/DataTable";
import { IStoreState, useAppDispatch, useAppSelector } from "../../../redux";
import { useNavigate } from "react-router-dom";
import { StandardTableActions } from "../../../Components/StandardTableActions/StandardTableActions";
import { UpsertPaymentsReceivedDialog } from "./dialogs/UpsertPaymentsReceivedDialog";
import { CustomButton } from "../../../Components/CustomButton";
import {
  IPaymentReceived,
  clearPaymentReceivedStateSync,
  defaultPaymentReceived,
  fetchPaymentReceivedListWithArgsAsync,
} from "../../../redux/accounting";
import { LoadState } from "../../../constants/enum";
import { useOutletContext } from "../../../providers";

export const PaymentsReceivedTable = () => {
  const {
    outlet_details: { outlet_uuid, outlet_name },
  } = useOutletContext();
  const {
    data: payments_received_list,
    totalRecords,
    loading,
  } = useAppSelector(
    (storeState: IStoreState) =>
      storeState.accounting.paymentReceived.payments_received_list
  );

  const [pagination, setPagination] = React.useState({
    pageNumber: 1,
    rowsPerPage: 10,
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [openUpsertDialog, setOpenUpsertDialog] =
    React.useState<IPaymentReceived | null>(null);

  const dataTableProps: IDataTableProps = {
    isPagination: true,
    totalRecords: totalRecords,
    rowsPerPageOptions: pagination.rowsPerPage,
    isDataLoading: loading !== LoadState.succeeded,
    mobileLogo: {
      type: MobileLogoRenderType.reactNode,
      onMobileLogoRender: (data: any) => {
        return (
          <Avatar
            sx={{
              width: 45,
              height: 45,
              color: "#fff",
              ml: "-8px",
              mr: 2,
            }}
          ></Avatar>
        );
      },
    },
    initialSortConfig: {
      insert_ts: {
        direction: "desc",
        filedName: "insert_ts",
      },
    },
    columns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "left",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPaymentReceived) => {
          return (
            <StandardTableActions
              onEditClick={() => setOpenUpsertDialog(row)}
            />
          );
        },
      },
      {
        key: "order_type",
        headerName: "Order Type",
        fieldName: "order_type",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
      },
      {
        key: "order_number",
        headerName: "Order Number",
        fieldName: "order_number",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
      },
      {
        key: "invoice_number",
        headerName: "Invoice Number",
        fieldName: "invoice_number",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
      },
      {
        key: "total_tax",
        headerName: "Total Tax",
        fieldName: "total_tax",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
      },
      {
        key: "total_amount",
        headerName: "Total Amount",
        fieldName: "total_amount",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
      },
      {
        key: "order_status",
        headerName: "Order Status",
        fieldName: "order_status",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
      },
      // {
      //   key: "insert_ts",
      //   headerName: "Date",
      //   fieldName: "insert_ts",
      //   enableSorting: true,
      //   renderType: RenderType.DATE_DARK_COLOR,
      //   exportCellWidth: 25,
      //   onExportRender: (values) => {
      //     return formatDateWithTime(values.value);
      //   },
      // },
      {
        key: "view",
        headerName: "View",
        fieldName: "",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPaymentReceived) => {
          return <Button variant="contained">View Invoice</Button>;
        },
      },
    ],

    items: payments_received_list,
    onPageChange: (newPageNumber: number) => {
      setPagination({ ...pagination, pageNumber: newPageNumber });
    },
    onRowsPerPageChange: (pageNumber: number, rowsPerPage: number) => {
      setPagination({
        pageNumber: pageNumber,
        rowsPerPage: rowsPerPage,
      });
    },
  };

  React.useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, outlet_uuid]);

  React.useEffect(() => {
    return () => {
      dispatch(clearPaymentReceivedStateSync());
    };
  }, []);

  const fetchList = () => {
    if (!outlet_uuid) return;
    dispatch(
      fetchPaymentReceivedListWithArgsAsync({
        outlet_uuid,
        page: pagination.pageNumber,
        rowsPerPage: pagination.rowsPerPage,
      })
    );
  };
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  return (
    <>
      <CustomButton
        variant="outlined"
        color="primary"
        sx={{
          display: "flex",
          marginLeft: "auto",
          marginY: 2,
          minWidth: "80px !important",
          backgroundColor: isLightMode
            ? "var(--secondary-color)"
            : "var(--primary-color)",
          color: isLightMode
            ? "var(--text-light-color)"
            : "var(--text-dark-color)",
          border: isLightMode
            ? "var(--border-light-color) 1px solid"
            : "var(--border-dark-color) 1px solid",
          "&:hover": {
            backgroundColor: isLightMode
              ? "var(--secondary-color)"
              : "var(--tertiary-color)",
            opacity: isLightMode ? 0.85 : 1,
            // color: isLightMode ? "var(--primary-color)" : "var(--secondary-color)",
            border: isLightMode
              ? "var(--border-light-color) 1px solid"
              : "var(--border-dark-color) 1px solid",
          },
        }}
        onClick={() =>
          setOpenUpsertDialog({
            ...defaultPaymentReceived,
            outlet_uuid,
            outlet_name,
          })
        }
      >
        Add New
      </CustomButton>
      <DataTable {...dataTableProps} />
      {openUpsertDialog && (
        <UpsertPaymentsReceivedDialog
          open={true}
          initialState={openUpsertDialog}
          onClose={() => setOpenUpsertDialog(null)}
        />
      )}
    </>
  );
};
